import React, {Fragment, useState} from "react";
import { SmartErrorDiv } from "./SmartErrorDiv";
import {EyeFill, EyeSlashFill} from "react-bootstrap-icons";

import './FormInputPassword.scss' ;

export const PasswordRules = {
    MIN_LENGTH: "minLength",
    REQUIRE_UPPERCASE: "requireUppercase",
    REQUIRE_LOWERCASE: "requireLowercase",
    REQUIRE_NUMERIC_CHAR: "requireNumericChar",
    REQUIRE_SPECIAL_CHAR: "requireSpecialChar",
};

export const defaultRules = [
    { type: PasswordRules.MIN_LENGTH, value: 8 },
    { type: PasswordRules.REQUIRE_UPPERCASE },
    { type: PasswordRules.REQUIRE_LOWERCASE },
    { type: PasswordRules.REQUIRE_NUMERIC_CHAR },
    // { type: PasswordRules.REQUIRE_SPECIAL_CHAR },
];

export const minRules = [
    { type: PasswordRules.MIN_LENGTH, value: 8 },
];

/**
 * react-hook-form User input field to implement password input and validation
 *
 * Implements a user input with optional hide/reveal password option, and
 *
 * @property array rules Optional rules object - see example below
 *
 * @example
 *
 * <FormInputPassword rules={defaultRules}
 *                     iconColor={iconColor}
 *                     readOnly={false}
 *                     label='Password'
 *                     placeHolder='Password'
 *                     sectionName={sectionName}
 *                     name={'password'}
 *                     register={register}
 *                     required={true}
 *                     maxLength={100}
 *                     autocomplete='off'
 *                     formErrors={formErrors}/>>
 */
const FormInputPassword = ( { register, iconColor, pattern, readOnly, sectionName, name, rules = [], className, formErrors, maxLength, required, placeHolder, autocomplete } ) =>
{
    const classText = className === undefined ? "form-control" : '' ;

    const [ type, setType ] = useState ( false ) ;

    const togglePasswordView = () =>
    {
        setType ( (type) => !type ) ;
    }

    const typeText = type ? 'text' : 'password' ;
    const eye = type ? <EyeSlashFill/> : <EyeFill/>

    const iconStyle = iconColor === undefined ? {} : { color: iconColor} ;

    function checkPassword(rules, password)
    {
        const failedRules = {};

        for (const rule of rules)
        {
            switch (rule.type)
            {
                case PasswordRules.MIN_LENGTH:
                    if (password.length < rule.value)
                    {
                        failedRules[rule.type] = {
                            failed: true,
                            message: `Password must be at least ${rule.value} characters long.`,
                        };
                    }
                    break;

                case PasswordRules.REQUIRE_NUMERIC_CHAR:
                    if (!/[0-9]/.test(password)) {
                        failedRules[rule.type] = {
                            failed: true,
                            message: "Password must include at least one numeric character.",
                        };
                    }
                    break;

                case PasswordRules.REQUIRE_UPPERCASE:
                    if (!/[A-Z]/.test(password))
                    {
                        failedRules[rule.type] = {
                            failed: true,
                            message: "Password must include at least one uppercase letter.",
                        };
                    }
                    break;

                case PasswordRules.REQUIRE_LOWERCASE:
                    if (!/[a-z]/.test(password))
                    {
                        failedRules[rule.type] = {
                            failed: true,
                            message: "Password must include at least one lowercase letter.",
                        };
                    }
                    break;

                case PasswordRules.REQUIRE_SPECIAL_CHAR:
                    if (!/[!@#$%^&*(),.?":{}|<>_\-+=~`[\]\\;/']/.test(password))
                    {
                        failedRules[rule.type] = {
                            failed: true,
                            message: "Password must include at least one special character.",
                        };
                    }
                    break;

                default:
                    failedRules[rule.type || "unknownRule"] = {
                        failed: true,
                        message: "Unknown password rule.",
                    };
            }
        }

        return {
            valid: Object.keys(failedRules).length === 0,
            failedRules,
        }
    }

    function getFailedRulesArray(result) {
        return Object.entries(result.failedRules).map(([rule, details]) => ({
            rule,
            ...details,
        }));
    }

    const checkPasswordRules = ( value ) =>
    {
        const checkResult = checkPassword(rules, value) ;

        if ( checkResult.valid === true )
        {
            return true ;
        }
        else
        {
            return getFailedRulesArray(checkResult).reduce ((accum,current) =>
            {
                if ( accum === null )
                {
                    if ( current.failed === true )
                    {
                        return current.message
                    }
                }

                return accum ;
            }, null)
        }
    }

    return (
        <Fragment>
            <div className="password-box">
                <input readOnly={readOnly}
                       autoComplete={autocomplete}
                       placeholder={placeHolder}
                       type={typeText}
                       className={classText} {...register(sectionName + '.' + name, {
                    required: required,
                    maxLength: maxLength,
                    pattern: pattern,
                    validate: checkPasswordRules
                })} />
                <i className='bi' onClick={togglePasswordView} style={iconStyle}>{eye}</i>
            </div>
            <SmartErrorDiv sectionName={sectionName} name={name} maxLength={maxLength} formErrors={formErrors}/>
        </Fragment>
)
}

export {
    FormInputPassword
} ;
